import { useEffect } from 'react'

/**
 * Stops the WINDOW from scrolling while the tab is mounted.
 *
 * The tab is sized against the viewport and scrolls internally (the tree rail
 * and the detail panel each have their own scroller). It renders inside the
 * shared `Page` / `ant-layout` wrappers, though, which are sized by their
 * content — so those wrappers end up taller than the viewport by roughly the
 * height of the chrome above the tab, and the window grows a second scrollbar
 * that scrolls past a pane which is already scrolling internally.
 *
 * Sizing the pane smaller to compensate does not work: a shorter pane shortens
 * the wrapper, which frees space, which lengthens the pane again — the
 * measurement diverges rather than settling.
 *
 * Nothing is lost by suppressing it. The overflow is empty wrapper box, not
 * content: with `body { overflow: hidden }` applied by hand the scrollbar
 * disappears and no part of the page becomes unreachable.
 *
 * Restores whatever the values were on unmount, rather than assuming they were
 * the CSS defaults, so leaving the tab cannot strand another screen without its
 * scrollbar.
 */
export const useSuppressWindowScroll = (enabled = true): void => {
  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    const { body, documentElement } = document
    const previousBody = body.style.overflow
    const previousRoot = documentElement.style.overflow

    body.style.overflow = 'hidden'
    documentElement.style.overflow = 'hidden'

    return () => {
      body.style.overflow = previousBody
      documentElement.style.overflow = previousRoot
    }
  }, [enabled])
}
